import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAccountScope } from "@/context/AccountScopeContext";
import {
  REPORT_DAYS, formatReportDate, ordinal, shortMonthNote,
} from "@/lib/monthlyReport";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { EnvelopeSimple, Eye, PaperPlaneTilt } from "@phosphor-icons/react";

// Radix Select cannot hold an empty value, so "every account" needs a token.
const ALL = "all";

const errorDetail = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  return typeof detail === "string" ? detail : fallback;
};

/**
 * Settings for the monthly email report: on or off, the day it arrives, and
 * which account it covers. Every change saves straight away, like a switch
 * should, and the server answers with the next send date.
 */
export default function MonthlyReportCard() {
  const { accounts } = useAccountScope();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  useEffect(() => {
    api.get("/reports/settings")
      .then((res) => setSettings(res.data))
      .catch(() => setSettings(false));
  }, []);

  const save = useCallback(async (changes) => {
    const next = { ...settings, ...changes };
    setSettings(next);
    setSaving(true);
    try {
      const res = await api.put("/reports/settings", {
        enabled: next.enabled,
        day_of_month: next.day_of_month,
        account_id: next.account_id || null,
      });
      setSettings(res.data);
    } catch (error) {
      setSettings(settings);
      toast.error(errorDetail(error, "Could not save the report settings"));
    } finally {
      setSaving(false);
    }
  }, [settings]);

  const accountParams = settings?.account_id ? { account_id: settings.account_id } : {};

  const openPreview = async () => {
    setLoadingPreview(true);
    try {
      const res = await api.get("/reports/preview", { params: accountParams });
      setPreview(res.data);
    } catch (error) {
      toast.error(errorDetail(error, "Could not build the preview"));
    } finally {
      setLoadingPreview(false);
    }
  };

  const sendTest = async () => {
    setSending(true);
    try {
      const res = await api.post("/reports/send-test", null, { params: accountParams });
      toast.success(`Test report sent to ${res.data.email}`);
    } catch (error) {
      toast.error(errorDetail(error, "Could not send the test report"));
    } finally {
      setSending(false);
    }
  };

  if (settings === null) return null;
  if (settings === false) {
    return (
      <Card className="bg-card border-border p-5 rounded-lg" data-testid="monthly-report-card">
        <h3 className="font-heading font-bold tracking-tight">Monthly report</h3>
        <p className="text-sm text-muted-foreground mt-1">Report settings are unavailable right now.</p>
      </Card>
    );
  }

  // A saved account deleted since falls back to every account on the server.
  const accountValue = accounts.some((a) => a.id === settings.account_id)
    ? settings.account_id
    : ALL;

  return (
    <Card className="bg-card border-border p-5 rounded-lg" data-testid="monthly-report-card">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <EnvelopeSimple size={18} />
            <h3 className="font-heading font-bold tracking-tight">Monthly report</h3>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Last month's income, spending and top insights, emailed to{" "}
            <span className="text-foreground break-all">{settings.email}</span>.
          </p>
        </div>
        <Switch
          checked={settings.enabled}
          disabled={saving}
          onCheckedChange={(enabled) => save({ enabled })}
          aria-label="Send monthly report"
          data-testid="monthly-report-toggle"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5">
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Day of the month</Label>
          <Select
            value={String(settings.day_of_month)}
            onValueChange={(value) => save({ day_of_month: Number(value) })}
            disabled={saving}
          >
            <SelectTrigger className="mt-1 bg-background border-border" data-testid="monthly-report-day">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-card border-border max-h-72">
              {REPORT_DAYS.map((day) => (
                <SelectItem key={day} value={String(day)}>{ordinal(day)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {shortMonthNote(settings.day_of_month) && (
            <p className="text-xs text-muted-foreground mt-1.5">{shortMonthNote(settings.day_of_month)}</p>
          )}
        </div>

        {accounts.length > 1 && (
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Accounts</Label>
            <Select
              value={accountValue}
              onValueChange={(value) => save({ account_id: value === ALL ? null : value })}
              disabled={saving}
            >
              <SelectTrigger className="mt-1 bg-background border-border" data-testid="monthly-report-account">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value={ALL}>All accounts</SelectItem>
                {accounts.map((account) => (
                  <SelectItem key={account.id} value={account.id}>{account.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <p className="text-sm mt-4" data-testid="monthly-report-next">
        {settings.enabled && settings.next_report_on ? (
          <>
            Next report on <span className="font-semibold">{formatReportDate(settings.next_report_on)}</span>
            <span className="text-muted-foreground">, covering {settings.next_report_month}.</span>
          </>
        ) : (
          <span className="text-muted-foreground">Reports are off.</span>
        )}
      </p>

      <div className="flex flex-wrap gap-2 mt-4">
        <Button
          size="sm"
          variant="outline"
          className="border-border gap-1.5"
          onClick={openPreview}
          disabled={loadingPreview}
          data-testid="monthly-report-preview"
        >
          <Eye size={15} /> {loadingPreview ? "Building…" : "Preview"}
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="border-border gap-1.5"
          onClick={sendTest}
          disabled={sending}
          data-testid="monthly-report-send-test"
        >
          <PaperPlaneTilt size={15} /> {sending ? "Sending…" : "Send test email"}
        </Button>
      </div>

      <Dialog open={Boolean(preview)} onOpenChange={(open) => !open && setPreview(null)}>
        <DialogContent className="bg-card border-border max-w-2xl p-0 overflow-hidden">
          <DialogHeader className="px-5 pt-5">
            <DialogTitle className="font-heading tracking-tight">Report preview</DialogTitle>
            <DialogDescription className="text-muted-foreground">{preview?.subject}</DialogDescription>
          </DialogHeader>
          {/* sandbox with no permissions: the email's links and markup stay inert here. */}
          <iframe
            title="Monthly report preview"
            sandbox=""
            srcDoc={preview?.html || ""}
            className="w-full h-[70vh] border-t border-border bg-[#050505]"
          />
        </DialogContent>
      </Dialog>
    </Card>
  );
}
